import { useState, useCallback } from 'react';
import { toast } from 'sonner';
import { apiListInfluentSamples, hasPhpBackend } from './phpApi';

export interface DbSample {
  id: number;
  sample_name: string;
  customer_name?: string;
  params?: Record<string, number>;
  created_at?: string;
}

export interface InfluentParams {
  flowRate: number;
  tss: number;
  og: number;
  bod: number;
  cod: number;
  ph: number;
  temp: number;
  alkalinity: number;
  turbidity: number;
  conductivity: number;
  hardness: number;
  nh3n: number;
  tp: number;
}

export const fmt = (v: any, d = 1) => (typeof v === 'number' && !isNaN(v) ? v : 0).toFixed(d);

/** Map raw database sample params to typed influent params with fallbacks */
export function mapSampleToInfluent(p: Record<string, number> | undefined): Partial<InfluentParams> {
  if (!p) return {};
  return {
    flowRate: Number(p.flowRate ?? p.flow_rate ?? 250),
    tss: Number(p.tss ?? 250),
    og: Number(p.oilGrease ?? p.oil_grease ?? p.og ?? 80),
    bod: Number(p.bod5 ?? p.bod ?? 1500),
    cod: Number(p.cod ?? 3000),
    ph: Number(p.ph ?? 7.0),
    temp: Number(p.temperature ?? 20),
    alkalinity: Number(p.alkalinity ?? p.alk ?? 100),
    turbidity: Number(p.turbidity ?? p.turb ?? p.ntu ?? 200),
    conductivity: Number(p.conductivity ?? p.cond ?? p.ec ?? 1500),
    hardness: Number(p.hardness ?? p.hard ?? p.total_hardness ?? 200),
    nh3n: Number(p.nh3n ?? p.nh3_n ?? p.ammonia ?? p.nh3 ?? 25),
    tp: Number(p.tp ?? p.totalPhosphorus ?? p.total_phosphorus ?? p.phosphorus ?? 5),
  };
}

/** React hook for loading database samples into any equipment spec page */
export function useSampleLoader<T extends InfluentParams>(
  setParams: (updater: (prev: T) => T) => void
) {
  const [dbSamples, setDbSamples] = useState<DbSample[]>([]);
  const [selectedSampleId, setSelectedSampleId] = useState<number | ''>('');
  const [baseSampleParams, setBaseSampleParams] = useState<Partial<InfluentParams> | null>(null);
  const [dilutionFactor, setDilutionFactor] = useState(1);
  const [loadingSamples, setLoadingSamples] = useState(false);

  const loadDbSamples = useCallback(async () => {
    if (!hasPhpBackend()) return;
    setLoadingSamples(true);
    try {
      // Use influent_samples table (correct endpoint, user-scoped server-side)
      const rows = await apiListInfluentSamples();
      const samples: DbSample[] = (Array.isArray(rows) ? rows : []).map((s: any) => ({
        id: Number(s.id),
        sample_name: s.sampleName || s.sample_name || 'Untitled',
        customer_name: s.customerName || s.customer_name || undefined,
        params: typeof s.params === 'string' ? JSON.parse(s.params) : (s.params || {}),
        created_at: s.createdAt || s.created_at,
      }));
      setDbSamples(samples);
    } catch {
      toast.error('Failed to load samples from database');
    } finally {
      setLoadingSamples(false);
    }
  }, []);

  const handleLoadSample = useCallback((id: number) => {
    const sample = dbSamples.find((s) => s.id === id);
    if (!sample?.params) {
      toast.error('Sample has no parameter data');
      return;
    }
    const mapped = mapSampleToInfluent(sample.params);
    setBaseSampleParams(mapped);
    setDilutionFactor(1);
    setSelectedSampleId(id);
    setParams((prev: T) => ({ ...prev, ...mapped } as T));
    toast.success(`Loaded sample: ${sample.sample_name}`);
  }, [dbSamples, setParams]);

  const applyDilution = useCallback((d: number) => {
    const safeD = Math.max(1, d);
    setDilutionFactor(safeD);
    if (!baseSampleParams) return;
    setParams((prev: T) => ({
      ...prev,
      flowRate: Number(baseSampleParams.flowRate || (prev as any).flowRate) * safeD,
      tss: Number(baseSampleParams.tss || (prev as any).tss) / safeD,
      og: Number(baseSampleParams.og || (prev as any).og) / safeD,
      bod: Number(baseSampleParams.bod || (prev as any).bod) / safeD,
      cod: Number(baseSampleParams.cod || (prev as any).cod) / safeD,
      ph: Number(baseSampleParams.ph || (prev as any).ph),
      temp: Number(baseSampleParams.temp || (prev as any).temp),
      alkalinity: Number(baseSampleParams.alkalinity || (prev as any).alkalinity),
      turbidity: Number(baseSampleParams.turbidity || (prev as any).turbidity) / safeD,
      conductivity: Number(baseSampleParams.conductivity || (prev as any).conductivity),
      hardness: Number(baseSampleParams.hardness || (prev as any).hardness),
      nh3n: Number(baseSampleParams.nh3n || (prev as any).nh3n) / safeD,
      tp: Number(baseSampleParams.tp || (prev as any).tp) / safeD,
    } as T));
  }, [baseSampleParams, setParams]);

  const clearSample = useCallback(() => {
    setSelectedSampleId('');
    setBaseSampleParams(null);
    setDilutionFactor(1);
  }, []);

  return {
    dbSamples, setDbSamples,
    selectedSampleId, setSelectedSampleId,
    baseSampleParams, setBaseSampleParams,
    dilutionFactor, setDilutionFactor,
    loadingSamples,
    loadDbSamples,
    handleLoadSample,
    applyDilution,
    clearSample,
  };
}

/** Common influent slider config for reuse across pages */
export const INFLUENT_SLIDERS = [
  { key: 'flowRate', label: 'Flow Rate', min: 50, max: 2000, step: 10, unit: 'm³/d', color: '#06b6d4' },
  { key: 'tss', label: 'TSS', min: 50, max: 3000, step: 10, unit: 'mg/L', color: '#f59e0b' },
  { key: 'og', label: 'O&G', min: 20, max: 1000, step: 10, unit: 'mg/L', color: '#f59e0b' },
  { key: 'bod', label: 'BOD', min: 100, max: 5000, step: 50, unit: 'mg/L', color: '#f97316' },
  { key: 'cod', label: 'COD', min: 200, max: 10000, step: 100, unit: 'mg/L', color: '#f97316' },
  { key: 'ph', label: 'Influent pH', min: 4, max: 9.5, step: 0.1, unit: '', color: '#a855f7' },
  { key: 'temp', label: 'Temperature', min: 5, max: 35, step: 1, unit: '°C', color: '#ef4444' },
  { key: 'alkalinity', label: 'Alkalinity', min: 10, max: 500, step: 5, unit: 'mg/L CaCO₃', color: '#10b981' },
  { key: 'turbidity', label: 'Turbidity', min: 10, max: 2000, step: 10, unit: 'NTU', color: '#f59e0b' },
  { key: 'conductivity', label: 'Conductivity', min: 100, max: 10000, step: 50, unit: 'μS/cm', color: '#fbbf24' },
  { key: 'hardness', label: 'Hardness', min: 10, max: 1000, step: 10, unit: 'mg/L CaCO₃', color: '#3b82f6' },
  { key: 'nh3n', label: 'NH₃-N', min: 1, max: 200, step: 1, unit: 'mg/L', color: '#8b5cf6' },
  { key: 'tp', label: 'Total Phosphorus', min: 0.5, max: 50, step: 0.5, unit: 'mg/L', color: '#ec4899' },
] as const;

/** Common profile defaults for all equipment pages */
export const COMMON_PROFILE_DEFAULTS = {
  dairy: { flowRate: 250, bod: 1850, cod: 3200, tss: 450, og: 120, ph: 6.8, temp: 25, alkalinity: 180, turbidity: 380, conductivity: 2500, hardness: 250, nh3n: 45, tp: 12 },
  brewery: { flowRate: 350, bod: 2200, cod: 4100, tss: 620, og: 180, ph: 5.2, temp: 20, alkalinity: 80, turbidity: 550, conductivity: 4200, hardness: 350, nh3n: 85, tp: 18 },
  fish: { flowRate: 150, bod: 1450, cod: 2650, tss: 520, og: 220, ph: 6.9, temp: 15, alkalinity: 220, turbidity: 480, conductivity: 1800, hardness: 180, nh3n: 35, tp: 8 },
};
